import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
    useAddConversationsMutation,
    useEditConversationsMutation,
    useGetConversationQuery,
} from "../../features/conversations/conversationsApi";
import { useGetUserQuery } from "../../features/users/usersApi";
import isValidEmail from "../../utils/isValidEmail";
import Error from "../Ui/Error";

const Modal = ({ open, control }) => {
    const [to, setTo] = useState("");
    const [message, setMessage] = useState("");
    const [userChecked, setUserChecked] = useState(false);
    const { user: loggedInUser } = useSelector((state) => state.auth) || {};
    const { email: myEmail } = loggedInUser || {};
    const [resError, setResError] = useState("");
    const navigate = useNavigate();
    const [getConversationApiCall, setGetConversationApiCall] = useState(false);

    const { data: participant } = useGetUserQuery(to, {
        skip: !userChecked,
        refetchOnMountOrArgChange: true,
    });
    const [addConversation] = useAddConversationsMutation();
    const [editConversation] = useEditConversationsMutation();
    const {
        data: conversationData,
        isSuccess: isGetConversationSuccess,
        isError: isGetConversationError,
        refetch,
    } = useGetConversationQuery(
        {
            userEmail: myEmail,
            participantEmail: participant?.length > 0 && participant[0]?.email,
        },
        {
            skip: !getConversationApiCall,
        }
    );

    useEffect(() => {
        if (participant?.length > 0 && participant[0]?.email !== myEmail) {
            refetch();
            setGetConversationApiCall(true);
        }
    }, [participant, myEmail, refetch]);

    const debouncedSearch = (fn, delay) => {
        let timeOutId;

        return (...args) => {
            clearTimeout(timeOutId);
            timeOutId = setTimeout(() => {
                fn(...args);
            }, delay);
        };
    };

    const doSearch = (value) => {
        if (isValidEmail(value)) {
            setTo(value);
            setUserChecked(true);
        }
    };

    const handleSearchUser = debouncedSearch(doSearch, 300);

    const handleSubmit = (e) => {
        e.preventDefault();

        if (isGetConversationError) {
            setResError("There was an error !");
        }

        if (isGetConversationSuccess && conversationData?.length > 0) {
            //edit conversation
            editConversation({
                sender: myEmail,
                id: conversationData[0].id,
                data: {
                    participants: `${myEmail}-${participant[0]?.email}`,
                    users: [loggedInUser, participant[0]],
                    message,
                    timestamp: new Date().getTime(),
                },
            })
                .unwrap()
                .then((res) => {
                    if (res) {
                        navigate(`/inbox/${res.id}`);
                        control();
                        setMessage("");
                        setTo("");
                    }
                });
        } else if (isGetConversationSuccess && conversationData?.length === 0) {
            //add conversation
            addConversation({
                sender: myEmail,
                data: {
                    participants: `${myEmail}-${participant[0]?.email}`,
                    users: [loggedInUser, participant[0]],
                    message,
                    timestamp: new Date().getTime(),
                },
            })
                .unwrap()
                .then((res) => {
                    if (res) {
                        navigate(`/inbox/${res.id}`);
                        control();
                        setMessage("");
                        setTo("");
                    }
                });
        }
    };

    return (
        open && (
            <>
                <div
                    onClick={control}
                    className="fixed w-full h-full inset-0 z-10 bg-black/50 cursor-pointer"
                ></div>
                <div className="rounded w-[400px] lg:w-[600px] space-y-8 bg-white p-10 absolute top-1/2 left-1/2 z-20 -translate-x-1/2 -translate-y-1/2">
                    <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
                        Send message
                    </h2>
                    <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
                        <input type="hidden" name="remember" value="true" />
                        <div className="rounded-md shadow-sm -space-y-px">
                            <div>
                                <label htmlFor="to" className="sr-only">
                                    To
                                </label>
                                <input
                                    id="to"
                                    name="to"
                                    type="email"
                                    required
                                    className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-violet-500 focus:border-violet-500 focus:z-10 sm:text-sm"
                                    placeholder="Send to"
                                    onChange={(e) =>
                                        handleSearchUser(e.target.value)
                                    }
                                />
                            </div>
                            <div>
                                <label htmlFor="message" className="sr-only">
                                    Message
                                </label>
                                <textarea
                                    id="message"
                                    name="message"
                                    type="text"
                                    required
                                    className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-violet-500 focus:border-violet-500 focus:z-10 sm:text-sm"
                                    placeholder="Message"
                                    value={message}
                                    onChange={(e) => setMessage(e.target.value)}
                                />
                            </div>
                        </div>

                        <div>
                            <button
                                type="submit"
                                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-violet-600 hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500"
                                disabled={
                                    conversationData === undefined ||
                                    (participant?.length > 0 &&
                                        participant[0]?.email) === myEmail
                                }
                            >
                                Send Message
                            </button>
                        </div>

                        {to.length > 0 && participant?.length === 0 && (
                            <Error message="This user does not exist !" />
                        )}
                        {participant?.length > 0 &&
                            participant[0]?.email === myEmail && (
                                <Error message="You can not send message to yourself !" />
                            )}
                        {resError && <Error message={resError} />}
                    </form>
                </div>
            </>
        )
    );
};

export default Modal;
